import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { DataSet, ColumnId, createColumnName } from '../../types';
import { TimeSeriesRangeConfig } from '../../types/graph-builder.types';
import { getColumnData, getColumnNameById, getColumnById } from '../utils/column-utils';
import { createDefaultCategoricalScale } from '../constants/palette-utils';
import { useD3Tooltip } from './useD3Tooltip';
import { useD3Zoom } from './useD3Zoom';
import { useD3Selection } from './useD3Selection';
import logger from '../utils/logger';

// D3要素の型定義
export interface D3ChartRefs {
  svg?: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g?: d3.Selection<SVGGElement, unknown, null, undefined>;
  clipPath?: d3.Selection<SVGRectElement, unknown, null, undefined>;
  xScale?: d3.ScaleTime<number, number> | d3.ScaleLinear<number, number>;
  yScale?: d3.ScaleLinear<number, number>;
  originalXScale?: d3.ScaleTime<number, number> | d3.ScaleLinear<number, number>;
  originalYScale?: d3.ScaleLinear<number, number>;
  xAxis?: d3.Selection<SVGGElement, unknown, null, undefined>;
  yAxis?: d3.Selection<SVGGElement, unknown, null, undefined>;
  legendGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
  colorScale?: d3.ScaleOrdinal<string | number, string>;
  pathGroups?: d3.Selection<SVGGElement, unknown, null, undefined>[];
  pointGroups?: d3.Selection<SVGGElement, unknown, null, undefined>[];
  areaGroups?: d3.Selection<SVGGElement, unknown, null, undefined>[];
  xColumnType?: 'date' | 'datetime' | 'numeric';  // X軸のカラムタイプ
  seriesData?: Array<{
    name: string;
    color: string;
    data: Array<{ xValue: Date | number; value: number; index: number }>;
    rangeData?: Array<{ xValue: Date | number; upper: number; lower: number; index: number }>;
  }>;
}

interface UseD3TimeSeriesChartProps {
  dataset: DataSet;
  dateColumn: ColumnId;
  valueColumns: ColumnId[];
  rangeColumns?: TimeSeriesRangeConfig[];
  rangeOpacity?: number;
  filteredRowIndices?: number[] | null;
  selectedRows?: number[];
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  onPointClick?: (dataIndices: number[], isAdditive?: boolean) => void;
}

/**
 * D3.jsを使った時系列チャートのカスタムフック
 * チャートの初期化、データ処理、インタラクションを統合
 */
export function useD3TimeSeriesChart({
  dataset,
  dateColumn,
  valueColumns,
  rangeColumns,
  rangeOpacity = 0.3,
  filteredRowIndices,
  selectedRows = [],
  width,
  height,
  margin = { top: 20, right: 30, bottom: 40, left: 50 },
  onPointClick
}: UseD3TimeSeriesChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const d3Refs = useRef<D3ChartRefs>({});
  
  // 実際のコンテナサイズの状態管理
  const [actualWidth, setActualWidth] = useState(width);
  const [actualHeight, setActualHeight] = useState(height);
  const [renderTrigger, setRenderTrigger] = useState(false);
  
  // カスタムフック使用
  const tooltip = useD3Tooltip({
    className: 'time-series-tooltip',
    style: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '8px',
      borderRadius: '4px',
      fontSize: '12px',
      pointerEvents: 'none' as const,
      whiteSpace: 'nowrap' as const,
      zIndex: 1000
    }
  });
  
  const selection = useD3Selection(selectedRows);
  
  // 凡例の高さ計算
  const isSingleSeries = valueColumns.length === 1;
  const legendRowCount = isSingleSeries ? 0 : Math.ceil(valueColumns.length / 3);
  const legendHeight = isSingleSeries ? 0 : legendRowCount * 16 + 10;
  
  const innerWidth = actualWidth - margin.left - margin.right;
  const innerHeight = actualHeight - margin.top - margin.bottom - legendHeight;

  // コンテナサイズの監視
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const containerHeight = entry.contentRect.height;
        setActualWidth(containerWidth > 0 ? containerWidth : width);
        setActualHeight(containerHeight > 0 ? containerHeight : height);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, height]);

  // チャート初期化
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    d3Refs.current.svg = svg;
    
    // 既存の要素をクリア
    svg.selectAll('*').remove();

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    d3Refs.current.g = g;

    // クリップパス
    const defs = svg.append('defs');
    d3Refs.current.clipPath = defs.append('clipPath')
      .attr('id', 'clip')
      .append('rect');

    // 軸
    d3Refs.current.xAxis = g.append('g')
      .attr('class', 'x-axis');
    
    d3Refs.current.yAxis = g.append('g')
      .attr('class', 'y-axis');

    // 凡例グループ
    d3Refs.current.legendGroup = g.append('g')
      .attr('class', 'legend-group');

    // スケール初期化（X軸タイプに応じて後で設定）
    d3Refs.current.yScale = d3.scaleLinear();
    d3Refs.current.originalYScale = d3.scaleLinear();

    // 色スケール
    d3Refs.current.colorScale = createDefaultCategoricalScale();

    // グループ配列
    d3Refs.current.pathGroups = [];
    d3Refs.current.pointGroups = [];
    d3Refs.current.areaGroups = [];

    return () => {
      // refsはリセットしない（他のuseEffectが依存しているため）
    };
  }, [margin.left, margin.top]); // 必要な依存関係を追加

  // サイズ更新
  useEffect(() => {
    const { svg, xScale, yScale, originalXScale, originalYScale, xAxis, legendGroup, clipPath } = d3Refs.current;

    if (svg) {
      svg.attr('width', actualWidth).attr('height', actualHeight);
    }

    if (clipPath) {
      clipPath.attr('width', innerWidth).attr('height', innerHeight);
    }

    if (xAxis) {
      xAxis.attr('transform', `translate(0,${innerHeight})`);
    }

    if (legendGroup) {
      legendGroup.attr('transform', `translate(${margin.left}, ${actualHeight - legendHeight + 10})`);
    }

    if (xScale && yScale && originalXScale && originalYScale) {
      xScale.range([0, innerWidth]);
      yScale.range([innerHeight, 0]);
      originalXScale.range([0, innerWidth]);
      originalYScale.range([innerHeight, 0]);
    }
  }, [actualWidth, actualHeight, innerWidth, innerHeight, legendHeight, margin]);

  // データ処理
  const processData = useCallback(() => {
    if (!dataset || (valueColumns.length === 0 && (!rangeColumns || rangeColumns.length === 0)) || 
        !d3Refs.current.yScale || 
        !d3Refs.current.colorScale || 
        !d3Refs.current.originalYScale) return null;

    const xColumnData = getColumnData(dataset, dateColumn);
    const xColumnSchema = getColumnById(dataset, dateColumn);
    const xColumnName = getColumnNameById(dataset, dateColumn);
    
    if (!xColumnData || !xColumnSchema) {
      logger.warn('[useD3TimeSeriesChart] No x-axis data found for column', { dateColumn, xColumnName });
      return null;
    }
    
    // X軸のタイプを判定
    const isNumericXAxis = xColumnSchema.type === 'int64' || xColumnSchema.type === 'float64';
    const xColumnType = isNumericXAxis ? 'numeric' : 
                        (xColumnSchema.type === 'date' || xColumnSchema.type === 'datetime') ? 'date' : 'date';
    
    // X軸スケールを設定（初回またはタイプ変更時）
    if (!d3Refs.current.xScale || d3Refs.current.xColumnType !== xColumnType) {
      d3Refs.current.xColumnType = xColumnType;
      if (isNumericXAxis) {
        d3Refs.current.xScale = d3.scaleLinear().range([0, innerWidth]);
        d3Refs.current.originalXScale = d3.scaleLinear().range([0, innerWidth]);
      } else {
        d3Refs.current.xScale = d3.scaleTime().range([0, innerWidth]);
        d3Refs.current.originalXScale = d3.scaleTime().range([0, innerWidth]);
      }
    }
    
    if (!d3Refs.current.xScale || !d3Refs.current.originalXScale) return null;
    
    const indices = filteredRowIndices || Array.from({ length: xColumnData.length }, (_, i) => i);

    // 各Y軸系列のデータを取得
    let seriesData = valueColumns.map(valueColumn => {
      const valueData = getColumnData(dataset, valueColumn);
      const valueColumnName = getColumnNameById(dataset, valueColumn);
      
      if (!valueData) {
        logger.warn('[useD3TimeSeriesChart] No value data found for column', { valueColumn, valueColumnName });
        return null;
      }

      const validPoints = indices
        .map(index => ({
          xRaw: xColumnData[index],
          value: valueData[index],
          index
        }))
        .filter(point => 
          point.xRaw !== null && 
          point.xRaw !== undefined && 
          point.value !== null && 
          point.value !== undefined &&
          typeof point.value === 'number'
        )
        .map(point => {
          if (isNumericXAxis) {
            return {
              xValue: Number(point.xRaw),
              value: point.value as number,
              index: point.index
            };
          } else {
            const dateValue = new Date(point.xRaw as string);
            return {
              xValue: dateValue,
              value: point.value as number,
              index: point.index
            };
          }
        })
        .filter(point => {
          if (isNumericXAxis) {
            return !isNaN(point.xValue as number) && !isNaN(point.value);
          } else {
            return !isNaN((point.xValue as Date).getTime()) && !isNaN(point.value);
          }
        })
        .sort((a, b) => {
          if (isNumericXAxis) {
            return (a.xValue as number) - (b.xValue as number);
          } else {
            return (a.xValue as Date).getTime() - (b.xValue as Date).getTime();
          }
        });

      return {
        name: valueColumnName || valueColumn,
        color: isSingleSeries ? '#4285F4' : d3Refs.current.colorScale!(valueColumn),
        data: validPoints,
        rangeData: undefined as Array<{ xValue: Date | number; upper: number; lower: number; index: number }> | undefined
      };
    }).filter(series => series !== null && series.data.length > 0);
    
    // すべての範囲を独立した系列として処理
    if (rangeColumns) {
      rangeColumns.forEach((rangeConfig, index) => {
        const upperData = getColumnData(dataset, rangeConfig.upperColumn);
        const lowerData = getColumnData(dataset, rangeConfig.lowerColumn);
        
        if (upperData && lowerData) {
          const rangeData = indices
            .map(index => ({
              xRaw: xColumnData[index],
              upper: upperData[index],
              lower: lowerData[index],
              index
            }))
            .filter(point => 
              point.xRaw !== null && 
              point.xRaw !== undefined && 
              point.upper !== null && 
              point.upper !== undefined &&
              point.lower !== null && 
              point.lower !== undefined &&
              typeof point.upper === 'number' &&
              typeof point.lower === 'number'
            )
            .map(point => {
              if (isNumericXAxis) {
                return {
                  xValue: Number(point.xRaw),
                  upper: point.upper as number,
                  lower: point.lower as number,
                  index: point.index
                };
              } else {
                const dateValue = new Date(point.xRaw as string);
                return {
                  xValue: dateValue,
                  upper: point.upper as number,
                  lower: point.lower as number,
                  index: point.index
                };
              }
            })
            .filter(point => {
              if (isNumericXAxis) {
                return !isNaN(point.xValue as number) && !isNaN(point.upper) && !isNaN(point.lower);
              } else {
                return !isNaN((point.xValue as Date).getTime()) && !isNaN(point.upper) && !isNaN(point.lower);
              }
            })
            .sort((a, b) => {
              if (isNumericXAxis) {
                return (a.xValue as number) - (b.xValue as number);
              } else {
                return (a.xValue as Date).getTime() - (b.xValue as Date).getTime();
              }
            });
          
          if (rangeData.length > 0) {
            // 範囲を独立系列として追加
            const rangeSeriesId = `range_${index}`;
            const color = rangeConfig.color || d3Refs.current.colorScale!(rangeSeriesId);

            seriesData.push({
              name: createColumnName(rangeConfig.label || `Range ${index + 1}`),
              color: color,
              data: [], // 範囲はラインを描画しない
              rangeData: rangeData as Array<{ xValue: Date | number; upper: number; lower: number; index: number }>
            });
          }
        }
      });
    }

    return seriesData as typeof d3Refs.current.seriesData;
  }, [dataset, dateColumn, valueColumns, rangeColumns, filteredRowIndices, isSingleSeries, innerWidth]);

  // ズームハンドラー
  const handleZoom = useCallback((transform: d3.ZoomTransform) => {
    const { originalXScale, originalYScale, xScale, yScale } = d3Refs.current;
    if (!originalXScale || !originalYScale || !xScale || !yScale) return;

    // スケールを更新
    const newXScale = transform.rescaleX(originalXScale);
    const newYScale = transform.rescaleY(originalYScale);
    
    xScale.domain(newXScale.domain());
    yScale.domain(newYScale.domain());

    // 再描画をトリガー（データ更新をトリガー）
    setRenderTrigger(prev => !prev);
  }, []);

  // useD3Zoomフックを使用
  const { zoomState, resetZoom } = useD3Zoom(svgRef as React.RefObject<SVGSVGElement>, handleZoom, {
    scaleExtent: [0.5, 20],
    extent: [[0, 0], [actualWidth, height]],
    translateExtent: [[-margin.left, -margin.top], [actualWidth + margin.right, height + margin.bottom]]
  });

  // チャート描画関数
  const renderChart = useCallback(() => {
    const { 
      g, xScale, yScale, xAxis, yAxis, legendGroup, 
      pathGroups, pointGroups, areaGroups, seriesData 
    } = d3Refs.current;
    
    if (!g || !xScale || !yScale || !xAxis || !yAxis || 
        !legendGroup || !pathGroups || !pointGroups || !areaGroups || !seriesData) return;

    // 軸の更新
    xAxis.call(d3.axisBottom(xScale));
    yAxis.call(d3.axisLeft(yScale));

    // 各系列の描画
    seriesData.forEach((series, seriesIndex) => {
      const pathGroup = pathGroups[seriesIndex];
      const pointGroup = pointGroups[seriesIndex];
      const areaGroup = areaGroups[seriesIndex];
      
      if (!pathGroup || !pointGroup || !areaGroup) return;

      // 範囲バンドの描画（ラインより先に描画）
      if (series.rangeData && series.rangeData.length > 0) {
        const area = d3.area<{ xValue: Date | number; upper: number; lower: number }>()
          .x(d => xScale(d.xValue))
          .y0(d => yScale(d.lower))
          .y1(d => yScale(d.upper))
          .curve(d3.curveMonotoneX);

        const areaPath = areaGroup.selectAll<SVGPathElement, typeof series.rangeData>('.range-area')
          .data([series.rangeData]);

        areaPath.enter()
          .append('path')
          .attr('class', 'range-area')
          .merge(areaPath as d3.Selection<SVGPathElement, typeof series.rangeData, SVGGElement, unknown>)
          .attr('d', area)
          .attr('fill', series.color)
          .attr('opacity', rangeOpacity)
          .attr('pointer-events', 'none');
      } else {
        // 範囲データがない場合は既存の範囲を削除
        areaGroup.selectAll('.range-area').remove();
      }

      // パスの描画（データがある場合のみ）
      if (series.data.length > 0) {
        const line = d3.line<{ xValue: Date | number; value: number }>()
          .x(d => xScale(d.xValue))
          .y(d => yScale(d.value))
          .curve(d3.curveMonotoneX);

        const path = pathGroup.selectAll<SVGPathElement, typeof series>('.line')
          .data([series]);

        path.enter()
          .append('path')
          .attr('class', 'line')
          .merge(path as d3.Selection<SVGPathElement, typeof series, SVGGElement, unknown>)
          .attr('d', d => line(d.data))
          .attr('fill', 'none')
          .attr('stroke', d => d.color)
          .attr('stroke-width', 2);
      } else {
        // データがない場合はラインを削除
        pathGroup.selectAll('.line').remove();
      }

      // ポイントの描画（データがある場合のみ）
      if (series.data.length > 0) {
        const points = pointGroup.selectAll<SVGCircleElement, typeof series.data[0]>('.point')
          .data(series.data, d => d.index);

      points.exit().remove();

      const pointsEnter = points.enter()
        .append('circle')
        .attr('class', 'point');

      points.merge(pointsEnter)
        .attr('cx', d => xScale(d.xValue))
        .attr('cy', d => yScale(d.value))
        .attr('r', d => selection.isSelected(d.index) ? 7 : 5)
        .attr('fill', series.color)
        .attr('stroke', d => selection.getStroke(d.index, 'white'))
        .attr('stroke-width', d => selection.getStrokeWidth(d.index, 1))
        .style('opacity', d => selection.getOpacity(d.index))
        .style('cursor', onPointClick ? 'pointer' : 'default')
        .on('mouseover', function(event: MouseEvent, d) {
          d3.select(this)
            .transition()
            .duration(100)
            .attr('r', 7);
          
          const xLabel = d3Refs.current.xColumnType === 'numeric' 
            ? `X: ${(d.xValue as number).toFixed(2)}` 
            : `Date: ${(d.xValue as Date).toLocaleDateString()}`;
          
          tooltip.show(
            `<div><strong>${series.name}</strong></div>
             <div>${xLabel}</div>
             <div>Value: ${d.value.toFixed(2)}</div>`,
            event
          );
        })
        .on('mousemove', (event: MouseEvent) => {
          tooltip.move(event);
        })
        .on('mouseout', function(_event: MouseEvent, d) {
          d3.select(this)
            .transition()
            .duration(100)
            .attr('r', selection.isSelected(d.index) ? 7 : 5);
          
          tooltip.hide();
        })
        .on('click', (event: MouseEvent, d) => {
          if (onPointClick) {
            const isAdditive = event.ctrlKey || event.metaKey;
            onPointClick([d.index], isAdditive);
          }
        });
      } else {
        // データがない場合はポイントを削除
        pointGroup.selectAll('.point').remove();
      }
    });

    // 凡例の描画
    if (!isSingleSeries && legendGroup) {
      // 凡例に表示する系列（データまたは範囲があるもの）
      const legendSeriesData = seriesData.filter(s => s.data.length > 0 || (s.rangeData && s.rangeData.length > 0));
      const legendItems = legendGroup.selectAll<SVGGElement, typeof seriesData[0]>('.legend-item')
        .data(legendSeriesData);

      legendItems.exit().remove();

      const legendEnter = legendItems.enter()
        .append('g')
        .attr('class', 'legend-item');

      legendEnter.append('rect')
        .attr('width', 10)
        .attr('height', 10);

      legendEnter.append('text')
        .attr('x', 15)
        .attr('y', 9)
        .style('font-size', '12px');

      legendItems.merge(legendEnter)
        .attr('transform', (_d, i) => {
          const col = i % 3;
          const row = Math.floor(i / 3);
          const x = col * (innerWidth / 3);
          const y = row * 16;
          return `translate(${x},${y})`;
        });

      legendItems.select('rect')
        .attr('fill', d => d.color);

      legendItems.select('text')
        .text(d => d.name);
    }
  }, [innerWidth, isSingleSeries, onPointClick, rangeOpacity, selection, tooltip]);

  // データ処理と描画
  useEffect(() => {
    const seriesData = processData();
    if (!seriesData) return;

    d3Refs.current.seriesData = seriesData;

    if (seriesData.length === 0) return;

    // 全系列の範囲を計算（データと範囲データを統合）
    const allXValues = seriesData.flatMap(s => [
      ...s.data.map(d => d.xValue),
      ...(s.rangeData?.map(d => d.xValue) || [])
    ]);
    const allValues = seriesData.flatMap(s => [
      ...s.data.map(d => d.value),
      ...(s.rangeData?.flatMap(d => [d.upper, d.lower]) || [])
    ]);
    
    // X軸と値の範囲を計算
    if (allXValues.length === 0 || allValues.length === 0) {
      logger.warn('[useD3TimeSeriesChart] No valid data or range data to display');
      return;
    }
    
    const xExtent = d3.extent(allXValues) as [Date | number, Date | number];
    const valueExtent = d3.extent(allValues) as [number, number];

    // Y軸に10%のマージンを追加
    const valueMargin = (valueExtent[1] - valueExtent[0]) * 0.1 || 1; // 値が同じ場合のフォールバック
    const adjustedValueExtent: [number, number] = [
      valueExtent[0] - valueMargin,
      valueExtent[1] + valueMargin
    ];

    if (d3Refs.current.originalXScale && d3Refs.current.originalYScale &&
        d3Refs.current.xScale && d3Refs.current.yScale) {
      d3Refs.current.originalXScale.domain(xExtent);
      d3Refs.current.originalYScale.domain(adjustedValueExtent);
      
      // ズーム状態を適用
      const transform = d3.zoomIdentity.translate(zoomState.x, zoomState.y).scale(zoomState.k);
      d3Refs.current.xScale.domain(transform.rescaleX(d3Refs.current.originalXScale).domain());
      d3Refs.current.yScale.domain(transform.rescaleY(d3Refs.current.originalYScale).domain());
    }

    // グループを必要に応じて作成
    const { g } = d3Refs.current;
    if (!g) return;

    const neededGroupCount = seriesData?.length || 0;
    const currentGroupCount = d3Refs.current.pathGroups?.length || 0;

    for (let i = currentGroupCount; i < neededGroupCount; i++) {
      // 範囲バンド用のグループ（最背面）
      const areaGroup = g.append('g')
        .attr('class', `area-group-${i}`)
        .attr('clip-path', 'url(#clip)');
      if (d3Refs.current.areaGroups) {
        d3Refs.current.areaGroups[i] = areaGroup;
      }

      const pathGroup = g.append('g')
        .attr('class', `path-group-${i}`)
        .attr('clip-path', 'url(#clip)');
      if (d3Refs.current.pathGroups) {
        d3Refs.current.pathGroups[i] = pathGroup;
      }

      const pointGroup = g.append('g')
        .attr('class', `point-group-${i}`)
        .attr('clip-path', 'url(#clip)');
      if (d3Refs.current.pointGroups) {
        d3Refs.current.pointGroups[i] = pointGroup;
      }
    }

    // 余分なグループを削除
    for (let i = neededGroupCount; i < currentGroupCount; i++) {
      d3Refs.current.areaGroups?.[i]?.remove();
      d3Refs.current.pathGroups?.[i]?.remove();
      d3Refs.current.pointGroups?.[i]?.remove();
    }

    // 配列のサイズを調整
    if (d3Refs.current.areaGroups) {
      d3Refs.current.areaGroups = d3Refs.current.areaGroups.slice(0, neededGroupCount);
    }
    if (d3Refs.current.pathGroups) {
      d3Refs.current.pathGroups = d3Refs.current.pathGroups.slice(0, neededGroupCount);
    }
    if (d3Refs.current.pointGroups) {
      d3Refs.current.pointGroups = d3Refs.current.pointGroups.slice(0, neededGroupCount);
    }

    // チャートを描画
    renderChart();
  }, [processData, renderChart, zoomState, renderTrigger]);

  return {
    svgRef,
    containerRef,
    actualWidth,
    actualHeight,
    resetZoom,
    tooltip,
    selection
  };
}